import {
  m2ft,
  m2km,
  m2mile,
  m2nmile,
  m2kft,
  mps2kmph,
  mps2mileph,
  mps2minpkm,
  mps2minpmile,
  mps2knot,
  minutesPerKm2MinutesPer100m,
  minutesPerKm2MinutesPer500m,
  minutesPerKm2MinutesPer100yd,
  mps2mpmin,
  mps2feetpmin,
  secondsToHours,
  kg2Lbs,
  m2yd,
  millibar2inHg,
  hz2bpm,
  j2kcal,
  k2c,
  k2f,
  rad2deg,
  rad2mil,
  hz2rpm,
} from './unitConverters';

/**
 * returns minutes and seconds with leading zeroes
 * of the given decimal minute value
 * @param  {Number}    decimalMinutes minutes to format, eg. 5.35
 * @return {String}    minutestring eg `05'21`
 */
function decimalMinutesToMinutesString(decimalMinutes) {
  if (decimalMinutes === 0) { return '0\'0'; }
  let minutes = Math.floor(decimalMinutes);
  let seconds = Math.floor((decimalMinutes % 1) * 60);
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }
  return `${minutes}'${seconds}`;
}

export function convertMeters(value, unit) {
  switch (unit) {
    case 'ft':
      return m2ft(value);

    case 'mi':
      return m2mile(value);

    case 'km':
      return m2km(value);

    case 'yd':
      return m2yd(value);

    case 'kft':
      return m2kft(value);

    case 'nmi':
      return m2nmile(value);

    default:
      return value;
  }
}

export function convertTime(value, format, unitSystem) {
  const time = new Date(value * 1000); // seconds to milliseconds
  const milliseconds = time.getUTCMilliseconds();

  // adding a 0 and calling .slice(-2) on the value will add 0 before the it if its value is less than 10.
  let seconds = parseInt(value, 10) % 60;
  let minutes = parseInt(value / 60, 10) % 60;
  const hours = time.getUTCHours();

  let absHours = parseInt(value / 3600, 10);

  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }

  const timeFormatter = () => {
    let displayHours = hours;
    if (unitSystem === 'imperial' && hours >= 12) {
      displayHours -= 12;
    }
    const timeString = format === '?h:mm' ? `${displayHours}:${minutes}` : `${displayHours}:${minutes}'${seconds}`;
    return timeString;
  };

  switch (format) {
    case 'ss':
      return value;

    case 'mm':
      return minutes;

    case '!h':
      return absHours;

    case 'ss.f':
      return `${seconds}.${Math.round(milliseconds / 100)}`;

    case "mm'ss": // eslint-disable-line
      return `${minutes}'${seconds}`;

    case "mm'ss.f": // eslint-disable-line
      return `${minutes}'${seconds}.${Math.round(milliseconds / 100)}`;

    case '!hh:mm': {
      if (absHours < 10) {
        absHours = `0${absHours}`;
      }
      return `${absHours}:${minutes}`;
    }

    case '!h:mm':
      return `${absHours}:${minutes}`;

    case "!h:mm'ss": // eslint-disable-line
      return `${absHours}:${minutes}'${seconds}`;

    case '?h:mm':
      return timeFormatter();

    case "?h:mm'ss": // eslint-disable-line
      return timeFormatter();

    default:
      return value;
  }
}

export function convertSpeed(value, format) {
  switch (format) {
    case 'km/h':
      return mps2kmph(value);

    case 'm/min':
      return mps2mpmin(value);

    case 'mi/h':
      return mps2mileph(value);

    case 'ft/min':
      return mps2feetpmin(value);

    case 'kn':
      return mps2knot(value);

    default:
      return value;
  }
}

export function convertPace(value, format) {
  const minpkm = mps2minpkm(value);
  const minpmile = mps2minpmile(value);
  const mp100m = minutesPerKm2MinutesPer100m(minpkm);
  const mp500m = minutesPerKm2MinutesPer500m(minpkm);
  const mp100yd = minutesPerKm2MinutesPer100yd(minpkm);

  switch (format) {
    case 's/km':
      return decimalMinutesToMinutesString(minpkm);

    case 's/mi':
      return decimalMinutesToMinutesString(minpmile);

    case 's/100m':
      return decimalMinutesToMinutesString(mp100m);

    case 's/500m':
      return decimalMinutesToMinutesString(mp500m);

    case 's/100yd':
      return decimalMinutesToMinutesString(mp100yd);

    default:
      return value;
  }
}

export function convertHeartRate(value, format) {
  switch (format) {
    case 'bpm':
    case 'rpm': // RPM is calculated with same formula as BPM, so this can be reused.
    case 'spm': // SPM is calculated with same formula as BPM, so this can be reused.
      return hz2bpm(value);
    default:
      return value;
  }
}

export function convertEnergy(value, format) {
  switch (format) {
    case 'kcal':
      return j2kcal(value);

    default:
      return value;
  }
}

export function convertCadence(value, format) {
  switch (format) {
    case 'rpm':
      return hz2rpm(value);

    default:
      return value;
  }
}

export function convertAirPressure(value, format) {
  switch (format) {
    case 'inHg':
      return millibar2inHg(value);

    case 'hPa':
      return value;

    default:
      return value;
  }
}

export function convertTemperature(value, format) {
  switch (format) {
    case 'C':
      return k2c(value);

    case 'F':
      return k2f(value);

    default:
      return value;
  }
}

export function convertRecoveryTime(value, format) {
  switch (format) {
    case 'h':
      return Math.floor(secondsToHours(value));

    default:
      return value;
  }
}

export function convertRad(value, format) {
  switch (format) {
    case 'deg':
      return rad2deg(value);

    case 'mil':
      return rad2mil(value);

    default:
      return value;
  }
}

export function convertWeight(value, format) {
  switch (format) {
    case 'kg':
      return value;

    case 'lb':
      return kg2Lbs(value);

    default:
      return value;
  }
}
